/**
 * Heroes Hooks
 *
 * React Query hooks for Miles and Passport.
 */

import { useQuery } from '@tanstack/react-query';
import { useSdkContext } from '../providers/SdkProvider';
import { useAuth } from '../providers/AuthProvider';
import { createHeroesService, type StampFilters } from '../services/heroes.service';

export const heroesKeys = {
  all: ['heroes'] as const,
  miles: () => [...heroesKeys.all, 'miles'] as const,
  milesHistory: (params?: { limit?: number; offset?: number }) =>
    [...heroesKeys.all, 'miles', 'history', params] as const,
  passport: () => [...heroesKeys.all, 'passport'] as const,
  timeline: (limit?: number) => [...heroesKeys.all, 'passport', 'timeline', limit] as const,
  badges: () => [...heroesKeys.all, 'badges'] as const,
  achievements: () => [...heroesKeys.all, 'achievements'] as const,
  stamps: (filters?: StampFilters) => [...heroesKeys.all, 'stamps', filters] as const,
  stampDetail: (stampId: string) => [...heroesKeys.all, 'stamps', stampId] as const,
  citizenBadges: () => [...heroesKeys.all, 'citizen-badges'] as const,
  citizenBadgeProgress: () => [...heroesKeys.all, 'citizen-badges', 'progress'] as const,
  journeyMiles: (params?: { limit?: number; offset?: number }) =>
    [...heroesKeys.all, 'journey-miles', params] as const,
  talentStamps: (talentId: string, journeyId: string, filters?: StampFilters) =>
    [...heroesKeys.all, 'talent', talentId, journeyId, 'stamps', filters] as const,
  talentCitizenBadges: (talentId: string, journeyId: string) =>
    [...heroesKeys.all, 'talent', talentId, journeyId, 'citizen-badges'] as const,
  talentJourneyMiles: (
    talentId: string,
    journeyId: string,
    params?: { limit?: number; offset?: number }
  ) => [...heroesKeys.all, 'talent', talentId, journeyId, 'journey-miles', params] as const,
};

/**
 * Get current Miles balance
 */
export function useMilesBalance(journeyInstanceId?: string) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const heroesService = createHeroesService(client);

  return useQuery({
    queryKey: [...heroesKeys.miles(), 'balance', journeyInstanceId],
    queryFn: () => heroesService.getMilesBalance(journeyInstanceId),
    enabled: isSignedIn && !!token,
  });
}

/**
 * Get Miles transaction history
 */
export function useMilesHistory(params?: { limit?: number; offset?: number }) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const heroesService = createHeroesService(client);

  return useQuery({
    queryKey: heroesKeys.milesHistory(params),
    queryFn: () => heroesService.getMilesHistory(params),
    enabled: isSignedIn && !!token,
  });
}

/**
 * Get user passport
 */
export function usePassport() {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const heroesService = createHeroesService(client);

  return useQuery({
    queryKey: heroesKeys.passport(),
    queryFn: () => heroesService.getPassport(),
    enabled: isSignedIn && !!token,
  });
}

/**
 * Get all badges
 */
export function useBadges() {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const heroesService = createHeroesService(client);

  return useQuery({
    queryKey: heroesKeys.badges(),
    queryFn: () => heroesService.getBadges(),
    enabled: isSignedIn && !!token,
  });
}

/**
 * Get all achievements
 */
export function useAchievements() {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const heroesService = createHeroesService(client);

  return useQuery({
    queryKey: heroesKeys.achievements(),
    queryFn: () => heroesService.getAchievements(),
    enabled: isSignedIn && !!token,
  });
}

/**
 * Get user's passport timeline (journey activity feed)
 */
export function usePassportTimeline(limit = 20) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const heroesService = createHeroesService(client);

  return useQuery({
    queryKey: heroesKeys.timeline(limit),
    queryFn: () => heroesService.getTimeline(limit),
    enabled: isSignedIn && !!token,
  });
}

/**
 * Get stamps with optional filters
 */
export function useStamps(filters?: StampFilters) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const heroesService = createHeroesService(client);

  return useQuery({
    queryKey: heroesKeys.stamps(filters),
    queryFn: () => heroesService.getStamps(filters),
    enabled: isSignedIn && !!token,
  });
}

/**
 * Get detailed info for a single stamp
 */
export function useStampDetail(stampId: string) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const heroesService = createHeroesService(client);

  return useQuery({
    queryKey: heroesKeys.stampDetail(stampId),
    queryFn: () => heroesService.getStampDetail(stampId),
    enabled: !!stampId && isSignedIn && !!token,
  });
}

/**
 * Get citizen badges with earned status
 */
export function useCitizenBadges() {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const heroesService = createHeroesService(client);

  return useQuery({
    queryKey: heroesKeys.citizenBadges(),
    queryFn: () => heroesService.getCitizenBadges(),
    enabled: isSignedIn && !!token,
  });
}

/**
 * Get citizen badge progress (on-demand evaluation)
 */
export function useCitizenBadgeProgress() {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const heroesService = createHeroesService(client);

  return useQuery({
    queryKey: heroesKeys.citizenBadgeProgress(),
    queryFn: () => heroesService.getCitizenBadgeProgress(),
    enabled: isSignedIn && !!token,
  });
}

/**
 * Get journey miles history and balance
 */
export function useJourneyMiles(params?: { limit?: number; offset?: number }) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const heroesService = createHeroesService(client);

  return useQuery({
    queryKey: heroesKeys.journeyMiles(params),
    queryFn: () => heroesService.getJourneyMiles(params),
    enabled: isSignedIn && !!token,
  });
}

/**
 * Business view: a specific talent's passport stamps (earned + locked), for
 * ONE of their journeys with the company — the drawer's (X8).
 * Same shape as `useStamps` so the Evidence triad renders identically.
 */
export function useStampsByTalent(
  talentId: string | undefined,
  journeyId: string,
  filters?: StampFilters
) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const heroesService = createHeroesService(client);

  return useQuery({
    queryKey: heroesKeys.talentStamps(talentId ?? '', journeyId, filters),
    queryFn: () => heroesService.getStampsByTalent(talentId as string, journeyId, filters),
    enabled: !!talentId && isSignedIn && !!token,
  });
}

/**
 * Business view: a specific talent's citizen badges (earned + locked), for one
 * of their journeys with the company — the drawer's (X8).
 */
export function useCitizenBadgesByTalent(talentId: string | undefined, journeyId: string) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const heroesService = createHeroesService(client);

  return useQuery({
    queryKey: heroesKeys.talentCitizenBadges(talentId ?? '', journeyId),
    queryFn: () => heroesService.getCitizenBadgesByTalent(talentId as string, journeyId),
    enabled: !!talentId && isSignedIn && !!token,
  });
}

/**
 * Business view: a specific talent's journey miles (drives the Levels triad),
 * for one of their journeys with the company — the drawer's (X8).
 */
export function useJourneyMilesByTalent(
  talentId: string | undefined,
  journeyId: string,
  params?: { limit?: number; offset?: number }
) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const heroesService = createHeroesService(client);

  return useQuery({
    queryKey: heroesKeys.talentJourneyMiles(talentId ?? '', journeyId, params),
    queryFn: () => heroesService.getJourneyMilesByTalent(talentId as string, journeyId, params),
    enabled: !!talentId && isSignedIn && !!token,
  });
}

/**
 * Get Miles breakdown by source (teleportal, qultureup, care, checkup, passport)
 */
export function useMilesBreakdown(journeyInstanceId?: string) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const heroesService = createHeroesService(client);

  return useQuery({
    queryKey: [...heroesKeys.miles(), 'breakdown', journeyInstanceId],
    queryFn: () => heroesService.getMilesBreakdown(journeyInstanceId),
    enabled: isSignedIn && !!token,
  });
}
